import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  Download,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Upload,
  FileText,
  X,
  Receipt,
  TrendingUp,
  TrendingDown,
  Calendar,
  Euro,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_METHODS = ["Abanca", "Tarjeta Abanca", "Wise", "Paypal", "Transferencia", "Efectivo"];
const INVOICE_STATUSES = ["Pagada", "No pagada", "Pagada 50%", "Con proforma"];

// Income specific options
const INCOME_PAYMENT_METHODS = ["Transferencia bancaria", "Paypal"];
const INCOME_STATUSES = ["Cobrada", "A la espera de cobro"];

const STATUS_COLORS = {
  "Pagada": "bg-green-100 text-green-800",
  "No pagada": "bg-red-100 text-red-800",
  "Pagada 50%": "bg-yellow-100 text-yellow-800",
  "Con proforma": "bg-blue-100 text-blue-800",
  "Cobrada": "bg-green-100 text-green-800",
  "A la espera de cobro": "bg-yellow-100 text-yellow-800",
};

// Translations
const translations = {
  es: {
    incomeInvoices: 'Facturas de Ingresos',
    newInvoice: 'Nueva Factura',
    editInvoice: 'Editar Factura',
    invoiceNumber: 'Nº Factura',
    orderNumber: 'Nº Pedido',
    clientName: 'Nombre Cliente',
    clientNif: 'NIF/CIF',
    invoiceDate: 'Fecha Factura',
    baseAmount: 'Base Imponible',
    iva: 'IVA %',
    irpf: 'IRPF %',
    total: 'Total',
    paymentMethod: 'Forma de Pago',
    status: 'Estado',
    description: 'Descripción',
    notes: 'Notas',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    noInvoices: 'No hay facturas de ingresos',
    uploadFile: 'Subir Archivo',
    files: 'Archivos',
    download: 'Descargar',
    downloadAll: 'Descargar Todo',
    lastMonth: 'Mes Pasado',
    customRange: 'Rango Personalizado',
    from: 'Desde',
    to: 'Hasta',
    totalIncome: 'Total Ingresos',
    actions: 'Acciones',
  },
  en: {
    incomeInvoices: 'Income Invoices',
    newInvoice: 'New Invoice',
    editInvoice: 'Edit Invoice',
    invoiceNumber: 'Invoice #',
    orderNumber: 'Order #',
    clientName: 'Client Name',
    clientNif: 'Tax ID',
    invoiceDate: 'Invoice Date',
    baseAmount: 'Base Amount',
    iva: 'VAT %',
    irpf: 'IRPF %',
    total: 'Total',
    paymentMethod: 'Payment Method',
    status: 'Status',
    description: 'Description',
    notes: 'Notes',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    noInvoices: 'No income invoices',
    uploadFile: 'Upload File',
    files: 'Files',
    download: 'Download',
    downloadAll: 'Download All',
    lastMonth: 'Last Month',
    customRange: 'Custom Range',
    from: 'From',
    to: 'To',
    totalIncome: 'Total Income',
    actions: 'Actions',
  }
};

// SortHeader component
const SortHeader = ({ field, children, sortField, sortOrder, onSort }) => (
  <TableHead 
    className="cursor-pointer hover:bg-muted/50 select-none"
    onClick={() => onSort(field)}
  >
    <div className="flex items-center gap-1">
      {children}
      {sortField === field ? (
        sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
      ) : (
        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
      )}
    </div>
  </TableHead>
);

export const IncomeInvoicesTab = ({ language = 'es' }) => {
  const t = translations[language] || translations.es;
  const fileInputRef = useRef(null);
  
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  
  // Download filter states
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [downloadDateFrom, setDownloadDateFrom] = useState('');
  const [downloadDateTo, setDownloadDateTo] = useState('');
  const [downloadPreset, setDownloadPreset] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    invoice_number: '',
    order_number: 0,
    client_name: '',
    client_nif: '',
    invoice_date: '',
    base_amount: 0,
    iva: 21,
    irpf: 0,
    total: 0,
    payment_method: '',
    status: 'A la espera de cobro',
    description: '',
    notes: ''
  });

  // Fetch invoices
  const fetchInvoices = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/income-invoices`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 50, sort_by: sortField, order: sortOrder }
      });
      setInvoices(response.data.invoices);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/income-invoices/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [sortField, sortOrder]);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Calculate total when base amount or taxes change
  useEffect(() => {
    const base = parseFloat(formData.base_amount) || 0;
    const ivaAmount = base * (parseFloat(formData.iva) || 0) / 100;
    const irpfAmount = base * (parseFloat(formData.irpf) || 0) / 100;
    const total = base + ivaAmount - irpfAmount;
    setFormData(prev => ({ ...prev, total: Math.round(total * 100) / 100 }));
  }, [formData.base_amount, formData.iva, formData.irpf]);

  // Open create dialog
  const openCreateDialog = () => {
    setEditingInvoice(null);
    setFormData({
      invoice_number: '',
      order_number: 0,
      client_name: '',
      client_nif: '',
      invoice_date: new Date().toISOString().split('T')[0],
      base_amount: 0,
      iva: 21,
      irpf: 0,
      total: 0,
      payment_method: '',
      status: 'A la espera de cobro',
      description: '',
      notes: ''
    });
    setDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      invoice_number: invoice.invoice_number || '',
      order_number: invoice.order_number || 0,
      client_name: invoice.client_name || '',
      client_nif: invoice.client_nif || '',
      invoice_date: invoice.invoice_date ? invoice.invoice_date.split('T')[0] : '',
      base_amount: invoice.base_amount || 0,
      iva: invoice.iva || 21,
      irpf: invoice.irpf || 0,
      total: invoice.total || 0,
      payment_method: invoice.payment_method || '',
      status: invoice.status || 'No pagada',
      description: invoice.description || '',
      notes: invoice.notes || ''
    });
    setDialogOpen(true);
  };

  // Save invoice
  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...formData,
        invoice_date: formData.invoice_date ? new Date(formData.invoice_date).toISOString() : null
      };
      
      if (editingInvoice) {
        await axios.put(`${API}/admin/income-invoices/${editingInvoice.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Factura actualizada');
      } else {
        await axios.post(`${API}/admin/income-invoices`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Factura creada');
      }
      
      setDialogOpen(false);
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error('Error al guardar factura');
    }
  };

  // Delete invoice
  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta factura?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/income-invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Factura eliminada');
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Error al eliminar factura');
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !uploadingFor) return;
    
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      await axios.post(`${API}/admin/income-invoices/${uploadingFor}/upload`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Archivo subido');
      fetchInvoices();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Error al subir archivo');
    } finally {
      setUploadingFor(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Download file
  const downloadFile = async (invoiceId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/income-invoices/${invoiceId}/files/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Error al descargar archivo');
    }
  };

  // Delete file
  const deleteFile = async (invoiceId, filename) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/income-invoices/${invoiceId}/files/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Archivo eliminado');
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Error al eliminar archivo');
    }
  };

  // Download all invoices
  const handleDownloadAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      
      if (downloadPreset === 'last_month') {
        params.preset = 'last_month';
      } else if (downloadDateFrom || downloadDateTo) {
        if (downloadDateFrom) params.date_from = downloadDateFrom;
        if (downloadDateTo) params.date_to = downloadDateTo;
      }
      
      const response = await axios.get(`${API}/admin/income-invoices/download`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ingresos_${downloadDateFrom || 'all'}_${downloadDateTo || 'all'}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setShowDownloadDialog(false);
      toast.success('Descarga iniciada');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Error al descargar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg"
        onChange={handleFileUpload}
      />

      {/* Header with stats */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-green-600" />
            {t.incomeInvoices}
          </h2>
          {stats && (
            <p className="text-muted-foreground">
              {t.totalIncome}: <span className="font-bold text-green-600">€{stats.total_amount?.toLocaleString() || 0}</span>
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowDownloadDialog(true)}>
            <Download className="h-4 w-4 mr-2" />
            {t.downloadAll}
          </Button>
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            {t.newInvoice}
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
              {t.noInvoices}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHeader field="invoice_number" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>
                    {t.invoiceNumber}
                  </SortHeader>
                  <TableHead>{t.clientName}</TableHead>
                  <SortHeader field="invoice_date" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>
                    {t.invoiceDate}
                  </SortHeader>
                  <SortHeader field="base_amount" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>
                    {t.baseAmount}
                  </SortHeader>
                  <SortHeader field="total" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>
                    {t.total}
                  </SortHeader>
                  <TableHead>{t.status}</TableHead>
                  <TableHead>{t.files}</TableHead>
                  <TableHead className="text-right">{t.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium">{invoice.invoice_number || '-'}</TableCell>
                    <TableCell>{invoice.client_name || '-'}</TableCell>
                    <TableCell>{invoice.invoice_date ? invoice.invoice_date.split('T')[0] : '-'}</TableCell>
                    <TableCell>€{invoice.base_amount?.toLocaleString() || 0}</TableCell>
                    <TableCell className="font-bold text-green-600">€{invoice.total?.toLocaleString() || 0}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[invoice.status] || 'bg-gray-100'}>
                        {invoice.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {invoice.attached_files?.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <FileText className="h-3 w-3 mr-1" />
                            {invoice.attached_files.length}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setUploadingFor(invoice.id);
                            fileInputRef.current?.click();
                          }}
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(invoice)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(invoice.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!pagination.has_prev}
            onClick={() => fetchInvoices(pagination.page - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="flex items-center px-4">
            {pagination.page} / {pagination.total_pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!pagination.has_next}
            onClick={() => fetchInvoices(pagination.page + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingInvoice ? t.editInvoice : t.newInvoice}</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t.invoiceNumber}</Label>
              <Input
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                placeholder="F-2024-001"
              />
            </div>
            <div className="space-y-2">
              <Label>{t.orderNumber}</Label>
              <Input
                type="number"
                value={formData.order_number}
                onChange={(e) => setFormData({ ...formData, order_number: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t.clientName}</Label>
              <Input
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t.clientNif}</Label>
              <Input
                value={formData.client_nif}
                onChange={(e) => setFormData({ ...formData, client_nif: e.target.value })}
                placeholder="B12345678"
              />
            </div>
            <div className="space-y-2">
              <Label>{t.invoiceDate}</Label>
              <Input
                type="date"
                value={formData.invoice_date}
                onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t.paymentMethod}</Label>
              <Select
                value={formData.payment_method}
                onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {INCOME_PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t.baseAmount}</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.base_amount}
                onChange={(e) => setFormData({ ...formData, base_amount: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t.iva}</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.iva}
                onChange={(e) => setFormData({ ...formData, iva: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t.irpf}</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.irpf}
                onChange={(e) => setFormData({ ...formData, irpf: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t.total}</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.total}
                readOnly
                className="bg-green-50 font-bold text-green-700"
              />
            </div>
            <div className="space-y-2">
              <Label>{t.status}</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INCOME_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-2">
              <Label>{t.description}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>{t.notes}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          {/* Show attached files if editing */}
          {editingInvoice && editingInvoice.attached_files?.length > 0 && (
            <div className="mt-4">
              <Label>{t.files}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {editingInvoice.attached_files.map((file) => (
                  <div key={file.filename} className="flex items-center gap-2 bg-muted px-3 py-1 rounded">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm">{file.original_name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => downloadFile(editingInvoice.id, file.filename)}
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-red-500"
                      onClick={() => deleteFile(editingInvoice.id, file.filename)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleSave}>
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download Dialog */}
      <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.downloadAll}</DialogTitle>
            <DialogDescription>
              Selecciona el rango de fechas para descargar las facturas
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                variant={downloadPreset === 'last_month' ? 'default' : 'outline'}
                onClick={() => {
                  setDownloadPreset('last_month');
                  setDownloadDateFrom('');
                  setDownloadDateTo('');
                }}
              >
                <Calendar className="h-4 w-4 mr-2" />
                {t.lastMonth}
              </Button>
              <Button
                variant={downloadPreset === '' && (downloadDateFrom || downloadDateTo) ? 'default' : 'outline'}
                onClick={() => setDownloadPreset('')}
              >
                {t.customRange}
              </Button>
            </div>
            
            {downloadPreset === '' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t.from}</Label>
                  <Input
                    type="date"
                    value={downloadDateFrom}
                    onChange={(e) => setDownloadDateFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t.to}</Label>
                  <Input
                    type="date"
                    value={downloadDateTo}
                    onChange={(e) => setDownloadDateTo(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDownloadDialog(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleDownloadAll}>
              <Download className="h-4 w-4 mr-2" />
              {t.download}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncomeInvoicesTab;
